import React from "react"
import PropTypes from "prop-types"
import { Column, SortDirection } from "react-virtualized"

import DataTable, { tooltipHeaderRenderer } from "@swipesense/data-table"
import CSVButton from "@swipesense/csv-button"

import PeakUsageGraph from "../PeakUsageGraph"
import Loading from "../Loading"
import UtilizationPercentSquare from "../UtilizationPercentSquare"

import "./UtilizationSummaryTable.scss"

class UtilizationSummaryTable extends React.PureComponent {
  peakUsageTrendRenderer = ({ cellData }) => {
    if (cellData.peakUsageTrend.length === 0) {
      return "-"
    } else {
      return (
        <PeakUsageGraph
          bare={true}
          maxInEntityCount={100} // Instead of graphing the peak usage as a count, we're actually graphing it as a percentage of all assets over time. Hence, the max is 100%.
          timeslices={cellData.peakUsageTrend}
        />
      )
    }
  }

  avgUsageRenderer = ({ cellData }) => UtilizationPercentSquare(cellData)

  assetRenderer = ({ cellData }) => {
    return (
      <span className="asset">
        {cellData.asset}
        <span className="max-in-entity">({cellData.maxInEntity})</span>
      </span>
    )
  }

  // The last row needs to be slightly taller so it isn't scrunched against the bottom.
  rowHeight = ({ index }) => {
    return index + 1 === this.props.data.length ? 60 : 44
  }

  rowClassName = ({ index }) => {
    return index + 1 === this.props.data.length ? "last" : ""
  }

  onRowClick = ({ rowData }) => {
    this.props.onRowClick(rowData)
  }

  render() {
    return (
      <div className="UtilizationSummaryTable">
        <CSVButton
          filename={`SwipeSense Asset Utilization Overview ${new Date().toLocaleDateString()}.csv`}
          data={this.props.data}
          headers={[
            "Asset",
            "# of Tagged Assets",
            "# Avail During Peak",
            "Avg Usage",
          ]}
          mapData={row => [
            row.asset,
            row.maxInEntity,
            row.availableDuringPeak,
            row.avgUsage,
          ]}
        />
        <DataTable
          data={this.props.data}
          rowHeight={this.rowHeight}
          rowClassName={this.rowClassName}
          sortBy={this.props.sortBy}
          sortDirection={this.props.sortDirection}
          onSortChange={this.props.onSortChange}
          onRowClick={this.props.onRowClick ? this.onRowClick : undefined}
          noRowsMessage="No data found. Try a new search or date range."
          loadingComponent={<Loading />}
          loading={this.props.loading}
        >
          <Column
            label="ASSET"
            width={1}
            dataKey="asset"
            cellDataGetter={({ rowData }) => rowData}
            cellRenderer={this.assetRenderer}
          />
          <Column
            label="# AVAILABLE DURING PEAK"
            width={1}
            dataKey="availableDuringPeak"
            headerRenderer={tooltipHeaderRenderer(
              '"# Available during peak" is the lowest number of assets available for use over your selected date range.'
            )}
          />
          <Column
            label="PEAK USAGE TREND"
            width={1}
            dataKey="peakUsageTrend"
            cellRenderer={this.peakUsageTrendRenderer}
            cellDataGetter={({ rowData }) => rowData}
            disableSort={true}
            headerRenderer={tooltipHeaderRenderer(
              '"Peak usage trend" represents your peak usage plotted over your selected date range.'
            )}
          />
          <Column
            label="AVG USAGE"
            width={1}
            dataKey="avgUsage"
            cellRenderer={this.avgUsageRenderer}
            headerRenderer={tooltipHeaderRenderer(
              '"Average usage" is the percent of time assets spent in a patient room, and likely in use, over your selected date range.'
            )}
          />
        </DataTable>
      </div>
    )
  }
}

UtilizationSummaryTable.defaultProps = {
  data: [],
  sortBy: "asset",
  sortDirection: SortDirection.ASC,
  loading: false,
}

UtilizationSummaryTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSortChange: PropTypes.func.isRequired,
  onRowClick: PropTypes.func,
  sortBy: PropTypes.string.isRequired,
  sortDirection: PropTypes.oneOf([SortDirection.ASC, SortDirection.DESC])
    .isRequired,
  loading: PropTypes.bool,
}

export default UtilizationSummaryTable
